import { useEffect, useState } from "react";
import { SOURCE_NAMES, editsMasterData, isDrinkProduct, isPantry, normalizeBarcode, pantryFieldsFromProduct, profileById } from "@lagerlog/core";
import type { ProductInfo } from "@lagerlog/core";
import { findArticles } from "../catalog.ts";
import type { Article, Catalog } from "../catalog.ts";
import { useStore } from "../hooks.ts";
import { failText } from "./EinkaufLink.tsx";
import { PantryForm } from "./Pantry.tsx";
import { Emoji } from "./Emoji.tsx";

type Answer = { product: ProductInfo | null; from: string | null; store?: boolean };

/**
 * A scanned code no article of the household has (docs/18-vorratsbuch.md, Strichcodes; F30):
 * what Open Food Facts knows about it (through the server), then „Als neuen Vorrat anlegen“ with
 * the form prefilled, or „Einem Vorrat zuordnen“. Either way the code stays at the article and
 * the article is chosen for booking. Owners and managers; others see what it is and who may.
 */
export function UnknownCode({ code: raw, catalog, onChosen, onClose }: { code: string; catalog: Catalog; onChosen: (id: string) => void; onClose: () => void }) {
  const { store } = useStore();
  const code = normalizeBarcode(raw);
  const keeper = editsMasterData(store.rights().role);
  const [answer, setAnswer] = useState<Answer | null>(null);
  const [error, setError] = useState<string | null>(code ? null : `„${raw}“ ist kein gültiger Strichcode.`);
  const [mode, setMode] = useState<"choose" | "assign" | "create">("choose");
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!code) return;
    let gone = false;
    store
      .request<Answer>(`/api/workspace/barcode/${code}`)
      .then((a) => !gone && setAnswer(a))
      .catch((e) => !gone && setError(failText(e)));
    return () => {
      gone = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  const product = answer?.product ?? null;
  const drink = product ? isDrinkProduct(product) : false;
  const kept = code ?? raw;

  async function assign(a: Article) {
    await store.edit("article", a.id, { barcodes: [...a.barcodes.filter((b) => b !== kept), kept] });
    onChosen(a.id);
  }

  if (mode === "create") {
    const preset = product && !drink ? pantryFieldsFromProduct(product) : { profile: "lebensmittel" };
    const { barcodes: _, ...fields } = preset;
    return <PantryForm profile={profileById(String(fields.profile))} preset={fields} extra={{ barcodes: [kept] }} onDone={(id) => (id ? onChosen(id) : setMode("choose"))} />;
  }

  const hits = query.trim().length >= 2 ? findArticles([...catalog.articles.values()].filter((a) => isPantry(a.fields)), query).slice(0, 8) : [];

  return (
    <section className="card" data-testid="unknown-code">
      <strong>Unbekannter Strichcode {kept}</strong>
      {code && !answer && !error && <p className="muted">Suche bei Open Food Facts …</p>}
      {error && <p className="error-box">{error} Zuordnen und anlegen geht trotzdem.</p>}
      {product && (
        <div className="row between" data-testid="unknown-code-product">
          <span className="named-emoji">
            <Emoji fields={pantryFieldsFromProduct(product)} />
            <span className="text">
              {product.brand ? `${product.brand.split(",")[0]} · ` : ""}
              {product.name}
              <br />
              <span className="muted">
                Daten von{" "}
                <a href={`https://world.${product.source}.org/product/${product.code}`} target="_blank" rel="noreferrer">
                  {SOURCE_NAMES[product.source]}
                </a>
                , Lizenz ODbL
              </span>
            </span>
          </span>
          <span className="side">{product.quantity ?? ""}</span>
        </div>
      )}
      {drink && <p className="notice">Ein Getränk: gehört in KellerLog, nicht in den Vorrat.</p>}
      {answer && !product && <p className="muted" data-testid="unknown-code-none">{answer.store ? "Ein Code der Ladenwaage oder des Ladens: Ihn kennt nur der Laden." : "Auch Open Food Facts kennt ihn nicht."}</p>}
      {!keeper && <p className="hint">Zuordnen und anlegen dürfen Inhaber und Verwaltung.</p>}
      {keeper && mode === "assign" && (
        <>
          <input autoFocus placeholder="Vorrat suchen" value={query} onChange={(e) => setQuery(e.target.value)} data-testid="unknown-code-search" />
          <ul className="list" data-testid="unknown-code-hits">
            {hits.map((a) => (
              <li key={a.id}>
                <button className="row between" onClick={() => void assign(a)}>
                  <span className="named-emoji">
                    <Emoji fields={a.fields} />
                    <span className="text">{a.name}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
      <div className="table-actions">
        {keeper && !drink && mode === "choose" && (
          <button className={product ? "primary" : ""} onClick={() => setMode("create")} data-testid="unknown-code-create">
            {product ? "Als neuen Vorrat anlegen" : "Neuen Vorrat anlegen"}
          </button>
        )}
        {keeper && mode === "choose" && (
          <button onClick={() => setMode("assign")} data-testid="unknown-code-assign">
            Einem Vorrat zuordnen
          </button>
        )}
        <button className="link" onClick={onClose} data-testid="unknown-code-close">
          Abbrechen
        </button>
      </div>
    </section>
  );
}
